import { Accordion, AccordionSummary, AccordionDetails, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { SectionHeader } from './SectionHeader';
import type { ReactNode } from 'react';
import { useReducedMotion } from '../../../../hooks/useReducedMotion';

interface BookingSectionProps {
  id: string;
  title: string;
  expanded: boolean;
  completed: boolean;
  summary?: string;
  subtitle?: string;
  disabled: boolean;
  onExpand: () => void;
  children: ReactNode;
  optional?: boolean;
  showConnector?: boolean;
  stepNumber?: number;
}

// Animation variants for premium feel
const containerVariants = {
  collapsed: {
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  collapsedCompleted: {
    boxShadow: '0 2px 8px rgba(99,102,241,0.15)',
  },
  expanded: {
    boxShadow: '0 8px 24px rgba(99,102,241,0.2)',
  },
};

export function BookingSection({
  title,
  expanded,
  completed,
  summary,
  subtitle,
  disabled,
  onExpand,
  children,
  optional,
  showConnector,
  stepNumber,
}: BookingSectionProps) {
  const reducedMotion = useReducedMotion();
  const theme = useTheme();

  // Determine animation state
  const animationState = expanded ? 'expanded' : completed ? 'collapsedCompleted' : 'collapsed';

  // Compute border props — optional sections get dashed border when not active/completed
  const isOptionalInactive = optional && !expanded && !completed;
  const borderWidth =
    expanded || completed ? '2px solid' : isOptionalInactive ? '1px dashed' : '1px solid';
  const borderColor =
    expanded || completed ? 'primary.main' : isOptionalInactive ? 'divider' : 'divider';

  return (
    <motion.div
      initial={false}
      animate={animationState}
      variants={containerVariants}
      transition={{ duration: reducedMotion ? 0.01 : 0.35, ease: 'easeOut' }}
      style={{ marginBottom: 16, borderRadius: 8, position: 'relative' }}
    >
      {/* Animated glow effect when expanded - disabled for reduced motion */}
      {expanded && !reducedMotion && (
        <motion.div
          animate={{
            boxShadow: [
              '0 0 0 0 rgba(99,102,241,0)',
              '0 0 0 8px rgba(99,102,241,0.08)',
              '0 0 0 0 rgba(99,102,241,0)',
            ],
          }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            inset: -4,
            borderRadius: 12,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}

      {/* Vertical connector line — desktop only, md+, hidden when expanded */}
      {showConnector && !expanded && (
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            left: 27,
            top: 72,
            bottom: -16,
            width: 2,
            bgcolor: completed ? 'primary.main' : 'divider',
            zIndex: 2,
          }}
        />
      )}

      <Accordion
        expanded={expanded}
        onChange={() => !disabled && onExpand()}
        disabled={disabled}
        sx={{
          borderRadius: '8px !important',
          '&:before': { display: 'none' },
          position: 'relative',
          zIndex: 1,
          // Gradient background for completed sections
          background:
            completed && !expanded
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${theme.palette.background.paper} 100%)`
              : disabled
                ? 'action.disabledBackground'
                : 'background.paper',
          // Border enhancement
          border: borderWidth,
          borderColor: borderColor,
          // Hover effect
          transition: 'all 0.2s ease',
          '&:hover:not(.Mui-disabled)': {
            borderColor: 'primary.light',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <AccordionSummary
          sx={{
            minHeight: { xs: 64, sm: 72 },
            '&.Mui-expanded': { minHeight: { xs: 64, sm: 72 } },
            cursor: disabled ? 'not-allowed' : 'pointer',
            // Gradient background for expanded header
            background: expanded
              ? 'linear-gradient(90deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.05) 100%)'
              : 'transparent',
          }}
        >
          <SectionHeader
            title={title}
            completed={completed}
            summary={summary}
            expanded={expanded}
            stepNumber={stepNumber}
            subtitle={subtitle}
            optional={optional}
          />
        </AccordionSummary>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: reducedMotion ? 0.01 : 0.35, ease: 'easeOut' }}
            >
              <AccordionDetails
                sx={{
                  pt: 0,
                  pb: 3,
                  px: { xs: 2, sm: 3 },
                }}
              >
                <motion.div
                  initial={reducedMotion ? { opacity: 0 } : { y: -20, opacity: 0 }}
                  animate={reducedMotion ? { opacity: 1 } : { y: 0, opacity: 1 }}
                  transition={{
                    duration: reducedMotion ? 0.01 : 0.3,
                    delay: reducedMotion ? 0 : 0.1,
                  }}
                >
                  {children}
                </motion.div>
              </AccordionDetails>
            </motion.div>
          )}
        </AnimatePresence>
      </Accordion>
    </motion.div>
  );
}
